import {useAgencyQuery} from "@/api/agencyApi";
import {useCompanyQuery} from "@/api/companyApi";
import TimesIcon from "@/assets/colorable/times.svg?react";
import Modal from "@/components/Common/Modal";
import ModalBreadcrumbs from "@/components/Common/ModalBreadcrumbs";
import AmbassadorProgramContent from "@/components/Common/referral/AmbassadorProgramContent";
import InviteAmbassadorsContent from "@/components/Common/referral/InviteAmbassadorsContent";
import ScopeAvatar from "@/components/Settings/ScopeAvatar";
import SettingsRail from "@/components/Settings/SettingsRail";
import AccountPasswordPane from "@/components/Settings/panes/AccountPasswordPane";
import AccountPreferencesPane from "@/components/Settings/panes/AccountPreferencesPane";
import AccountProfilePane from "@/components/Settings/panes/AccountProfilePane";
import AdminBillingPane from "@/components/Settings/panes/AdminBillingPane";
import AdminSettingsPane from "@/components/Settings/panes/AdminSettingsPane";
import AgencyAdminSettingsPane from "@/components/Settings/panes/AgencyAdminSettingsPane";
import AgencyInfoPane from "@/components/Settings/panes/AgencyInfoPane";
import AmbassadorsPane from "@/components/Settings/panes/AmbassadorsPane";
import BillingPane from "@/components/Settings/panes/BillingPane";
import InfoPane from "@/components/Settings/panes/InfoPane";
import UsersPane from "@/components/Settings/panes/UsersPane";
import AddMoreBuyoutsSubview from "@/components/Settings/subviews/AddMoreBuyoutsSubview";
import CreateBrandSubview from "@/components/Settings/subviews/CreateBrandSubview";
import ManageCreditsSubview from "@/components/Settings/subviews/ManageCreditsSubview";
import PaymentFailedSubview from "@/components/Settings/subviews/PaymentFailedSubview";
import {usePaymentFailedInvoices} from "@/hooks/usePaymentFailedInvoices";
import {useSettingsNavigation} from "@/hooks/useSettingsNavigation";
import {useSettingsScopeRows} from "@/hooks/useSettingsScopeRows";
import {useSettingsSectionItems} from "@/hooks/useSettingsSectionItems";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import CancelPlanContent from "@/pages/plans/CancelPlanContent";
import {PermissionProvider} from "@/permissions";
import {useSettingsAgencyStore} from "@/store/agency";
import {useSettingsCompanyStore} from "@/store/company";
import {SETTINGS_ACCOUNT_SECTIONS, SETTINGS_ADMIN_SECTIONS, SETTINGS_SCOPE_SECTIONS} from "@/store/constants";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import type {TSettingsSubview, TSettingsSubviewType} from "@/store/types";
import useUserStore from "@/store/user";
import {useLocation, useNavigate, useRouter} from "@tanstack/react-router";
import {useAtom} from "jotai";
import {useEffect, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useMedia, useUnmount, useUpdateEffect} from "react-use";

const SettingsModal: FC = () => {
  const {t} = useTranslation(["common", "multi_account", "payments", "referral", "settings"]);
  const [settingsModal, setSettingsModal] = useAtom(settingsModalAtom);
  const navigateSettings = useSettingsNavigation();
  const isAdmin = useIsAdmin();
  const isUserLoaded = useUserStore(state => !!state.user.id);
  const selfAgencies = useUserStore(state => state.user.agencies);
  const selfSubBrands = useUserStore(state => state.user.sub_brands);
  const {activeRow, agency, flatRow} = useSettingsScopeRows({scope: settingsModal?.scope ?? null});
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const sectionItems = useSettingsSectionItems();
  const {getPaymentFailedInvoices} = usePaymentFailedInvoices();
  const isMobileView = useMedia("(max-width: 640px)");
  const [isRailShown, setRailShown] = useState(false);
  const resetAgency = useSettingsAgencyStore(state => state.reset);
  const resetCompany = useSettingsCompanyStore(state => state.reset);
  const setAgency = useSettingsAgencyStore(state => state.setAgency);
  const setCompany = useSettingsCompanyStore(state => state.setCompany);

  const settingsScope = settingsModal?.scope;
  const isViewingSelf =
    settingsScope?.type === "agency"
      ? !!selfAgencies?.some(member => member.agency?.id === settingsScope.id)
      : !!selfSubBrands?.some(subBrand => subBrand.company.id === settingsScope?.id);
  const canOpenSettings = !!settingsScope && (isViewingSelf || isAdmin);
  const isForbiddenSection =
    !isAdmin && SETTINGS_ADMIN_SECTIONS.some(adminSection => adminSection === settingsModal?.section);

  const {data: agencyData, isFetchedAfterMount: isAgencyFetched} = useAgencyQuery({
    agencyId: canOpenSettings && settingsScope?.type === "agency" ? settingsScope.id : 0,
    queryOptions: {refetchOnMount: true},
  });
  const {data: companyData, isFetchedAfterMount: isCompanyFetched} = useCompanyQuery({
    companyId: canOpenSettings && settingsScope?.type === "brand" ? settingsScope.id : 0,
    queryOptions: {refetchOnMount: true},
  });

  useEffect(() => {
    if (agencyData && isAgencyFetched) setAgency(agencyData);
  }, [agencyData, isAgencyFetched]);

  useEffect(() => {
    if (companyData && isCompanyFetched) setCompany(companyData);
  }, [companyData, isCompanyFetched]);

  const unmaskUrl = () => {
    if (router.state.location.maskedLocation) navigate({to: ".", replace: true, search: true});
  };

  useUnmount(() => {
    resetAgency();
    resetCompany();
    unmaskUrl();
  });

  useEffect(() => {
    if (!canOpenSettings || !settingsModal || isForbiddenSection) return;

    const {scope} = settingsModal;
    const scopeSection = SETTINGS_SCOPE_SECTIONS.find(section => section === settingsModal.section);

    if (!scopeSection) {
      unmaskUrl();
      return;
    }

    navigate({
      to: ".",
      replace: true,
      search: true,
      state: {settingsMask: {scope, section: scopeSection}},
      mask:
        scope.type === "agency"
          ? {
              to: "/agency/$agencyId/edit",
              params: {agencyId: scope.id},
              search: {section: scopeSection},
            }
          : {
              to: "/company/$companyId/edit",
              params: {companyId: scope.id},
              search: {section: scopeSection},
            },
    });
  }, [canOpenSettings, isForbiddenSection, settingsModal?.scope.id, settingsModal?.scope.type, settingsModal?.section]);

  useUpdateEffect(() => setSettingsModal(null), [pathname]);

  useEffect(() => {
    if (!isUserLoaded || !settingsModal) return;

    if (!canOpenSettings) setSettingsModal(null);
    else if (isForbiddenSection) setSettingsModal({scope: settingsModal.scope, section: "info"});
  }, [canOpenSettings, isForbiddenSection, isUserLoaded, settingsModal]);

  if (!settingsModal || !canOpenSettings || isForbiddenSection) return null;

  const {scope, section: settingsSection, subview} = settingsModal;

  const isAccountSection = SETTINGS_ACCOUNT_SECTIONS.some(accountSection => accountSection === settingsSection);
  const eyebrowRow = !flatRow && !isAccountSection ? activeRow : null;

  const isAgencyScope = scope.type === "agency";
  const subviewTitles: Record<TSettingsSubviewType, string> = {
    add_more_buyouts: t("payments:EXTRA_BUYOUTS_add_more"),
    add_more_campaigns: t("payments:EXTRA_CAMPAIGNS_add_more"),
    create_brand: t(agency?.large_brand ? "multi_account:new_company" : "multi_account:new_customer"),
    ambassador_program: t("referral:ambassador_program"),
    cancel_plan: t("common:cancelPlan"),
    invite_ambassadors: t("referral:invite_ambassadors"),
    manage_credits: t(
      subview?.type === "manage_credits" && subview.mode === "remove"
        ? "common:remove_credits_from_account"
        : "common:add_credits_to_account",
    ),
    payment_failed: t("common:failedToProcessPayment"),
  };

  const subviewTrail: TSettingsSubview[] = !subview
    ? []
    : subview.type === "invite_ambassadors"
      ? [{type: "ambassador_program"}, subview]
      : [subview];

  const renderSubview = () => {
    switch (subview?.type) {
      case "add_more_buyouts":
      case "add_more_campaigns":
        return (
          <AddMoreBuyoutsSubview
            onClose={() => setSettingsModal({scope, section: settingsSection})}
            scope={scope}
            type={subview.type === "add_more_buyouts" ? "EXTRA_BUYOUTS" : "EXTRA_CAMPAIGNS"}
          />
        );
      case "create_brand":
        return (
          <CreateBrandSubview
            agencyId={agency?.id}
            isLargeBrand={agency?.large_brand}
            onClose={() => setSettingsModal({scope, section: settingsSection})}
            onCreated={companyId => setSettingsModal({scope: {id: companyId, type: "brand"}, section: "info"})}
          />
        );
      case "manage_credits":
        return (
          <ManageCreditsSubview
            mode={subview.mode}
            onClose={() => setSettingsModal({scope, section: settingsSection})}
            scope={scope}
          />
        );
      case "ambassador_program":
        return (
          <AmbassadorProgramContent
            agencyId={isAgencyScope ? scope.id : null}
            cardsClassName="max-lg:sm:flex-col"
            onSelect={section =>
              setSettingsModal({scope, section: settingsSection, subview: {section, type: "invite_ambassadors"}})
            }
          />
        );
      case "invite_ambassadors":
        return (
          <InviteAmbassadorsContent
            agencyId={isAgencyScope ? scope.id : null}
            companyId={isAgencyScope ? null : scope.id}
            onSectionChange={section =>
              setSettingsModal({scope, section: settingsSection, subview: {section, type: "invite_ambassadors"}})
            }
            section={subview.section}
          />
        );
      case "cancel_plan":
        return <CancelPlanContent onClose={() => setSettingsModal({scope, section: settingsSection})} />;
      case "payment_failed":
        return (
          <PaymentFailedSubview
            invoices={getPaymentFailedInvoices({scope})}
            onPaid={() => setSettingsModal({scope, section: settingsSection})}
          />
        );
      default:
        return null;
    }
  };

  const renderPane = () => {
    switch (settingsSection) {
      case "account_password":
        return <AccountPasswordPane />;
      case "account_preferences":
        return <AccountPreferencesPane />;
      case "account_profile":
        return <AccountProfilePane />;
      default:
        break;
    }

    switch (settingsSection) {
      case "admin_billing":
        return <AdminBillingPane scope={scope} />;
      case "admin_settings":
        return isAgencyScope ? (
          <AgencyAdminSettingsPane key={scope.id} scope={scope} />
        ) : (
          <AdminSettingsPane key={scope.id} scope={scope} />
        );
      case "ambassadors":
        return <AmbassadorsPane scope={scope} />;
      case "billing":
        return <BillingPane key={`${scope.type}-${scope.id}`} scope={scope} />;
      case "info":
        return isAgencyScope ? (
          <AgencyInfoPane key={scope.id} scope={scope} />
        ) : (
          <InfoPane key={scope.id} scope={scope} />
        );
      case "users":
        return <UsersPane scope={scope} />;
    }
  };

  return (
    <Modal
      backgroundClassName="h-full max-h-full w-full max-w-full overflow-hidden rounded-none sm:h-171 sm:max-h-[92vh] sm:w-260 sm:max-w-[96vw] sm:rounded-2xl md:h-171 md:max-h-[92vh] md:w-260"
      className="min-h-0 flex-1 flex-row gap-0 overflow-hidden p-0"
      onClose={() => navigateSettings(null)}
      shouldHideBottomRow
      shouldHideModalTitle
      shouldLockBodyScroll={false}>
      <PermissionProvider companyId={scope.type === "brand" ? scope.id : null}>
        {(!isMobileView || isRailShown) && (
          <div className="flex min-h-0 w-full flex-col sm:contents">
            {isMobileView && (
              <div className="border-neutral-grey-light-dark flex h-14 shrink-0 items-center justify-between border-b px-6">
                <span className="text-neutral-black text-sm font-medium">{t("common:Settings")}</span>
                <button aria-label={t("common:close")} onClick={() => navigateSettings(null)}>
                  <TimesIcon className="text-neutral-grey-light size-5" />
                </button>
              </div>
            )}
            <SettingsRail
              onSelect={({scope: nextScope, section: nextSection, subview: nextSubview}) => {
                const isCurrentView =
                  (nextScope ?? scope).id === scope.id &&
                  (nextScope ?? scope).type === scope.type &&
                  nextSection === settingsSection &&
                  nextSubview?.type === subview?.type;

                if (!isCurrentView) {
                  navigateSettings({scope: nextScope ?? scope, section: nextSection, subview: nextSubview});
                }
                setRailShown(false);
              }}
              scope={scope}
              section={settingsSection}
            />
          </div>
        )}
        <div className={clsxm("min-w-0 flex-1 flex-col", isMobileView && isRailShown ? "hidden" : "flex")}>
          <div className="border-neutral-grey-light-dark flex h-14 shrink-0 items-center justify-between border-b px-6">
            {subview || (isMobileView && !isRailShown) ? (
              <ModalBreadcrumbs
                className="p-0"
                crumbs={
                  subview
                    ? subviewTrail.map(crumb => ({
                        label: subviewTitles[crumb.type],
                        onClick: () => navigateSettings({scope, section: settingsSection, subview: crumb}),
                      }))
                    : [{label: sectionItems[settingsSection].label}]
                }
                onBack={() => {
                  if (!subview) {
                    setRailShown(true);
                    return;
                  }

                  navigateSettings({scope, section: settingsSection, subview: subviewTrail[subviewTrail.length - 2]});
                }}
              />
            ) : (
              <div />
            )}
            <button aria-label={t("common:close")} onClick={() => navigateSettings(null)}>
              <TimesIcon className="text-neutral-grey-light hover:text-neutral-grey-dark size-5 transition-colors duration-150" />
            </button>
          </div>
          <div
            className={clsxm("flex flex-col gap-4 overflow-y-auto py-6", subview?.type !== "create_brand" && "px-8")}>
            {subview ? (
              renderSubview()
            ) : (
              <>
                {eyebrowRow && (
                  <div className="flex items-center gap-2">
                    <ScopeAvatar className="size-5 rounded" name={eyebrowRow.name} picture={eyebrowRow.picture} />
                    <span className="text-neutral-grey-dark text-2xs leading-none font-semibold tracking-wider uppercase">
                      {eyebrowRow.name}
                    </span>
                  </div>
                )}
                {renderPane()}
              </>
            )}
          </div>
        </div>
      </PermissionProvider>
    </Modal>
  );
};

export default SettingsModal;
